import * as React from "react"
import { RefObject } from "react"

import { detectScrollCompleted } from "shared/utils/browser"

/**
 * Scrolls the scrollDivRef to the selectedId element. By default, we only scroll if the element is not visible.
 * @param scrollDivRef The ref of the div with the scrollbar (e.g. the div with overflow: auto)
 * @param selectedId Elements are identified by their data-item-id attribute
 * @param options { position } position: 0 = top, 1 = bottom, 0.2 = 20% from top.
 * @param options { force } force: true = scroll even if the element is already visible
 * @param onScrollCompleted Called when the scroll has completed, or immediately if there is no scroll
 */
const useAutoscroll = (
  scrollDivRef: RefObject<HTMLDivElement>,
  selectedId: string | undefined,
  options?: { position?: number; force?: boolean },
  onScrollCompleted?: () => void,
): void => {
  const { position = 0.2, force = false } = options || {}

  React.useEffect(() => {
    const scrollDiv = scrollDivRef.current
    if (!scrollDiv || selectedId === undefined) return

    const item = (itemId: string | "first"): Element => {
      const item = scrollDiv.querySelector(itemId === "first" ? "[data-item-id]" : `[data-item-id="${itemId}"]`)
      if (!item) throw `Item ${itemId} not found`
      return item
    }

    const { height: scrollDivHeight } = scrollDiv.getBoundingClientRect()
    const { top: itemTop, bottom: itemBottom } = item(selectedId).getBoundingClientRect()
    const { top: firstItemTop } = item("first").getBoundingClientRect()

    // Scroll positions where the element would be (respectively) aligned to the top or bottom of the viewport
    const scrollMin = itemBottom - firstItemTop - scrollDivHeight
    const scrollMax = itemTop - firstItemTop
    const scrollTo = scrollMin * position + scrollMax * (1 - position)

    const outOfView = scrollDiv.scrollTop < scrollMin || scrollDiv.scrollTop > scrollMax
    const needScroll = outOfView || force

    if (needScroll) {
      const smoothScroll = Math.abs(scrollTo - scrollDiv.scrollTop) <= scrollDivHeight
      scrollDiv.scroll({ top: scrollTo, behavior: smoothScroll ? "smooth" : "auto" })
    }

    const interval = detectScrollCompleted(scrollDiv, scrollTo, needScroll, onScrollCompleted)
    return () => interval && clearInterval(interval)
  }, [force, onScrollCompleted, position, scrollDivRef, selectedId])
}

export default useAutoscroll
